import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import SupplierContext from "@contexts/SupplierContext";
import QBDQueueActionEnum from "@models/QBDQueueActionEnum";
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router";
import { QuickbooksDesktopSetupDataPullModal } from "../QuickbooksDesktopSetupDataPullModal/QuickbooksDesktopSetupDataPullModal";
import IQBDDataPullDTO from "@models/DTO/IQBDDataPullDTO";
import UserContext from "@contexts/UserContext";
import { SocketContext } from "@contexts/SocketContext";
import EndpointsEnum from "@models/EndpointsEnum";
import UiContext from "@contexts/UiContext";
import IQBDQueueOptions from "@models/IQBDQueueOptions";
import IQBD_Queue from "@models/IQBD_Queue";
import ISupplier_Integration from "@models/ISupplier_Integration";
import IUpdateSupplierIntegrationsDTO from "@models/DTO/IUpdateSupplierIntegrationsDTO";
import IQBDActionsOnNextUpdate from "@models/QuickBooks Desktop/IQBDActionsOnNextUpdate";
import IQBDResetQueueDTO from "@models/DTO/IQBDResetQueueDTO";
import IQBDGetQueueItemsDTO from "@models/DTO/IQBDGetQueueItemsDTO";
import IQBDOnGetQueueItemsDTO from "@models/DTO/IQBDOnGetQueueItemsDTO";
import IGetSupplierDTO from "@models/DTO/IGetSupplierDTO";
import ICatalog from "@models/ICatalog";
import { startCase } from "lodash-es";
import ICatalog_Item from "@models/ICatalog_Item";
import IQBDRemoveQueueItemsDTO from "@models/DTO/IQBDRemoveQueueItemsDTO";
import IQBDOnRemoveQueueItemsDTO from "@models/DTO/IQBDOnRemoveQueueItemsDTO";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import INotification from "@models/INotification";
import IUiSocketSetup from "@models/IUiSocketSetup";
import IOnGetOrderNamesByGuidDTO from "@models/DTO/IOnGetOrderNamesByGuidDTO";
import IGetOrderNamesByGuidDTO from "@models/DTO/IGetOrderNamesByGuidDTO";
import IGetVendorsByGuidDTO from "@models/DTO/IGetVendorsByGuidDTO";
import IOnGetVendorsByGuidDTO from "@models/DTO/IOnGetVendorsByGuidDTO";
import IOnGetSupplierDTO from "@models/DTO/IOnGetSupplierDTO";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";
import { VerticalLine } from "@components/shared/VerticalLine/VerticalLine";
import { useAppContextNonNull } from "@contexts/AppContext";
import IQBDGetXmlDTO from "@models/DTO/IQBDGetXmlDTO";
import IQBDOnGetXmlDTO from "@models/DTO/IQBDOnGetXmlDTO";

type Props = {
    borderClass: string;
    catalogs: ICatalog[];
};

export const QuickbooksDesktopQueue = (props: Props) => {
    const { userModel, supplierModel, socket, uiContext } = useAppContextNonNull();

    const [activeManualSync, setActiveManualSync] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [hasActionChanges, setHasActionChanges] = useState(false);
    const [isActiveRefreshQueue, setIsActiveRefreshQueue] = useState(false);

    const [actionsOnNextUpdate, setActionsOnNextUpdate] = useState<IQBDActionsOnNextUpdate>(
        supplierModel.Supplier_Integration.qbd_ActionsOnNextUpdate || ({} as IQBDActionsOnNextUpdate)
    );
    const [orderNamesByGuid, setOrderNamesByGuid] = useState<{ guid: string; name: string }[]>([]);
    const [customerDetailsByGuid, setCustomerDetailsByGuid] = useState<{ guid: string; name: string }[]>([]);

    const catalogItems = actionsOnNextUpdate?.create?.CatalogItem || [];
    const updateCatalogItems = actionsOnNextUpdate?.update?.CatalogItem || [];

    const [modalComponent, setModalComponent] = useState<React.ReactNode | null>(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const [readQueueItems, setReadQueueItems] = useState<IQBD_Queue[]>([]);
    const [createQueueItems, setCreateQueueItems] = useState<IQBD_Queue[]>([]);
    const [updateQueueItems, setUpdateQueueItems] = useState<IQBD_Queue[]>([]);

    const manualSyncInitialData = (queueOptions: { [key: string]: boolean }, queueItemsOptions?: IQBDQueueOptions) => {
        const payload: IQBDDataPullDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queueOptions,
            queueItemsOptions,
        };

        socket.emit(EndpointsEnum.QBDPerformInitialDataPull, payload);

        setActiveManualSync(true);
    };

    const showDataPullModal = () => {
        const modalComponent = <QuickbooksDesktopSetupDataPullModal closeModalFn={closeModal} setDataPullFn={manualSyncInitialData} />;

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const resetQueue = () => {
        const payload: IQBDResetQueueDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.QBDResetQueue, payload);

        setActiveManualSync(true);
    };

    const onResetQueue = () => {
        setActiveManualSync(false);

        if (supplierModel.Supplier_Integration.qbd_use_queue_table) {
            getQueueItems();
        } else {
            getSupplier();
        }
    };

    const getQueueItems = () => {
        if (supplierModel.Supplier_Integration.qbd_use_queue_table && supplierModel.Supplier_Integration.has_qbd) {
            const payload: IQBDGetQueueItemsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
            };

            socket.emit(EndpointsEnum.QBDGetQueueItems, payload);
        }
    };

    const onGetQueueItems = (data: IQBDOnGetQueueItemsDTO) => {
        setReadQueueItems(data.read);
        setCreateQueueItems(data.create);
    };

    const saveActionChanges = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            Supplier_Integration: {
                qbd_ActionsOnNextUpdate: actionsOnNextUpdate,
            } as ISupplier_Integration,
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);

        setUpdating(true);
        setHasActionChanges(false);
    };

    const resetActionChanges = () => {
        setActionsOnNextUpdate(supplierModel.Supplier_Integration.qbd_ActionsOnNextUpdate || ({} as IQBDActionsOnNextUpdate));
        setHasActionChanges(false);
    };

    const getSupplier = () => {
        const payload: IGetSupplierDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.SupplierGetSupplier, payload);
    };

    const removeAction = (
        actionType: "read" | "create" | "push",
        value:
            | "Customers"
            | "TermOptions"
            | "CatalogItems"
            | "SalesTaxOptions"
            | "SalesTaxItems"
            | "COGSAccountOptions"
            | "AssetAccountOptions"
            | "IncomeAccountOptions"
            | "SalesOrder"
            | "Invoice"
            | "Catalog"
            | "Templates"
            | "SalesReps"
            | "ItemSubTotals"
            | "PriceLevels"
            | "UnitOfMeasureOptions"
            | "ItemSites"
            | "Class"
            | "ShipMethod"
            | "DataExtDefs",
        guid?: string
    ) => {
        const _actionsOnNextUpdate = actionsOnNextUpdate;

        if (actionType === "read") {
            // @ts-expect-error Has issues with types
            delete _actionsOnNextUpdate[actionType][value];
        } else if (actionType === "push") {
            if (value === "Catalog") {
                _actionsOnNextUpdate.push = {
                    ..._actionsOnNextUpdate,
                    Catalog: [],
                };
            }
            if (value === "Customers") {
                _actionsOnNextUpdate.push = {
                    ..._actionsOnNextUpdate,
                    Customers: false,
                };
            }
        } else {
            if (value === "Customers") {
                // All creates are Guid Arrays of the given value
                // @ts-expect-error Has issues with types
                const filteredArr = _actionsOnNextUpdate[actionType].Customer.filter((itemGuid: string) => {
                    if (itemGuid !== guid) {
                        return itemGuid;
                    }
                });

                _actionsOnNextUpdate[actionType].Customer = filteredArr;
            } else {
                // All creates are Guid Arrays of the given value
                // @ts-expect-error Has issues with types
                const filteredArr = _actionsOnNextUpdate[actionType][value].filter((itemGuid: string) => {
                    if (itemGuid !== guid) {
                        return itemGuid;
                    }
                });

                // @ts-expect-error Has issues with types
                _actionsOnNextUpdate[actionType][value] = filteredArr;
            }
        }

        setActionsOnNextUpdate(_actionsOnNextUpdate);
        setHasActionChanges(true);
    };

    const getCatalogNameFromPushCatalog = () => {
        if (actionsOnNextUpdate?.push?.Catalog && props.catalogs) {
            if ((actionsOnNextUpdate.push.Catalog || []).length) {
                const foundCatalog = props.catalogs.find((catalog) => {
                    if (actionsOnNextUpdate.push.Catalog) {
                        for (const catalogGuid of actionsOnNextUpdate.push.Catalog) {
                            return catalog.guid === catalogGuid;
                        }
                    }
                });

                if (foundCatalog) {
                    return foundCatalog.name;
                }
            }
        }

        return null;
    };

    const processActionOptions = (item: IQBD_Queue) => {
        let option: string | null | undefined;

        const optionsArrayPreCompile: { prefix: string; value: string }[] = [];

        if (item.qbd_action === QBDQueueActionEnum.READ_INVOICES) {
            option = item.options?.Invoices?.fromTxnDate;
        } else if (item.qbd_action === QBDQueueActionEnum.READ_SALES_ORDERS) {
            if (item.options?.SalesOrders?.fromTxnDate) {
                optionsArrayPreCompile.push({ prefix: "From", value: item.options?.SalesOrders?.fromTxnDate });
            }

            if (item.options?.SalesOrders?.toTxnDate) {
                optionsArrayPreCompile.push({ prefix: "To", value: item.options?.SalesOrders?.toTxnDate });
            }

            if (item.options?.SalesOrders?.refNumber) {
                optionsArrayPreCompile.push({ prefix: "Ref. No.", value: item.options?.SalesOrders?.refNumber.join(", ") });
            }

            option = optionsArrayPreCompile
                .map((optionItem) => {
                    return `${optionItem.prefix}: ${optionItem.value}`;
                })
                .join(", ");
        } else if (item.qbd_action === QBDQueueActionEnum.READ_CUSTOMERS) {
            if (item.options?.Customers?.autoProcess) {
                option = "Auto Process";
            } else {
                option = "Manual Process";
            }
        } else if (item.qbd_action === QBDQueueActionEnum.READ_PRODUCTS) {
            if (item.options?.Products?.autoProcess) {
                option = "Auto Process";
            } else {
                option = "Manual Process";
            }
        } else if (item.qbd_action === QBDQueueActionEnum.READ_PURCHASE_ORDERS) {
            if (item.options?.PurchaseOrders?.fromTxnDate) {
                optionsArrayPreCompile.push({ prefix: "From", value: item.options?.PurchaseOrders?.fromTxnDate });
            }

            if (item.options?.PurchaseOrders?.toTxnDate) {
                optionsArrayPreCompile.push({ prefix: "To", value: item.options?.PurchaseOrders?.toTxnDate });
            }

            if (item.options?.PurchaseOrders?.refNumber) {
                optionsArrayPreCompile.push({ prefix: "Ref. No.", value: item.options?.PurchaseOrders?.refNumber.join(", ") });
            }

            option = optionsArrayPreCompile
                .map((optionItem) => {
                    return `${optionItem.prefix}: ${optionItem.value}`;
                })
                .join(", ");
        }

        if (option) {
            return <span>{option}</span>;
        } else {
            return null;
        }
    };

    const processActionName = (action: string) => {
        if (action === QBDQueueActionEnum.READ_DATA_EXT_DEFS) {
            return "Custom Fields";
        }

        return startCase(action.replaceAll("_", " ").toLowerCase());
    };

    const removeQueueItem = (guid: string) => {
        const payload: IQBDRemoveQueueItemsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queueItemGuid: [guid],
        };

        socket.emit(EndpointsEnum.QBDRemoveQueueItems, payload);
    };

    const onRemoveQueueItems = (data: IQBDOnRemoveQueueItemsDTO) => {
        const notification: INotification = {
            message: "successfully_removed_queue_item",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        getQueueItems();
    };

    const onQBDPerformInitialDataPull = (data: IUpdateSupplierIntegrationsDTO) => {
        const notification: INotification = {
            level: NotificationLevelEnum.success,
            title: "successfully_set_data_pull",
            position: NotificationPositionEnum.br,
        };

        uiContext.notifyFn(null, notification);

        setActiveManualSync(false);

        if (supplierModel.Supplier_Integration.qbd_use_queue_table) {
            getQueueItems();
        } else {
            getSupplier();
        }
    };

    const getOrderNamesByGuid = () => {
        const invoiceGuids = supplierModel.Supplier_Integration.qbd_ActionsOnNextUpdate?.create?.Invoice || [];
        const salesOrderGuids = supplierModel.Supplier_Integration.qbd_ActionsOnNextUpdate?.create?.SalesOrder || [];

        const payload: IGetOrderNamesByGuidDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuids: [...invoiceGuids, ...salesOrderGuids],
        };

        socket.emit(EndpointsEnum.OrderGetOrderNamesByGuid, payload);
    };

    const onGetOrderNamesByGuid = (data: IOnGetOrderNamesByGuidDTO) => {
        const guidArr: { name: string; guid: string }[] = (data.orders || []).map((order) => {
            return {
                guid: order!.guid,
                name: order!.name || "",
            };
        });

        setOrderNamesByGuid(guidArr);
    };

    const getVendorsByGuid = () => {
        const vendorGuids = supplierModel.Supplier_Integration.qbd_ActionsOnNextUpdate?.create?.Customer || [];

        if (vendorGuids.length) {
            const payload: IGetVendorsByGuidDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                guids: vendorGuids,
            };

            socket.emit(EndpointsEnum.VendorsGetVendorsByGuid, payload);
        }
    };

    const onGetVendorsByGuid = (data: IOnGetVendorsByGuidDTO) => {
        const guidArr: { name: string; guid: string }[] = (data.vendors || []).map((vendor) => {
            return {
                guid: vendor!.guid,
                name: vendor!.name || "",
            };
        });

        setCustomerDetailsByGuid(guidArr);
    };

    const onGetSupplier = (data: IOnGetSupplierDTO) => {
        setActionsOnNextUpdate(data.supplierModel.Supplier_Integration.qbd_ActionsOnNextUpdate || ({} as IQBDActionsOnNextUpdate));
    };

    const onGetQbdXmlData = (data: IQBDOnGetXmlDTO) => {
        console.log("onGetQbdXmlData", data);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.QBDOnResetQueue, fn: onResetQueue },
        { endpoint: EndpointsEnum.QBDOnGetQueueItems, fn: onGetQueueItems },
        { endpoint: EndpointsEnum.QBDOnRemoveQueueItems, fn: onRemoveQueueItems },
        { endpoint: EndpointsEnum.QBDOnPerformInitialDataPull, fn: onQBDPerformInitialDataPull },
        { endpoint: EndpointsEnum.OrderOnGetOrderNamesByGuid, fn: onGetOrderNamesByGuid },
        { endpoint: EndpointsEnum.VendorsOnGetVendorsByGuid, fn: onGetVendorsByGuid },
        { endpoint: EndpointsEnum.SupplierOnGetSupplier, fn: onGetSupplier },
        { endpoint: EndpointsEnum.QBDOnGetXML, fn: onGetQbdXmlData },
    ];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        getOrderNamesByGuid();
        getQueueItems();
        getSupplier();
        getVendorsByGuid();

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    const getOrderXml = (item: IQBD_Queue) => {
        if (item.qbd_action === QBDQueueActionEnum.CREATE_SALES_ORDER && item.order_guid) {
            getQbdXmlData("SALES_ORDER", item.order_guid);
        }

        if (item.qbd_action === QBDQueueActionEnum.CREATE_INVOICE && item.order_guid) {
            getQbdXmlData("INVOICE", item.order_guid);
        }
    };

    const getQbdXmlData = (type: "SALES_ORDER" | "INVOICE", guid: string) => {
        if (userModel) {
            const payload: IQBDGetXmlDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                type,
                typeGuid: guid,
            };

            socket.emit(EndpointsEnum.QBDGetXML, payload);

            console.log("Getting XML Data for Order", guid);
        }
    };

    return (
        <div className={"QuickbooksDesktopQueue mt-3 mb-5"}>
            {supplierModel.Supplier_Integration.qbd_use_queue_table ? (
                <div className={"mb-4"}>
                    <div className={"d-flex mb-2"}>
                        <OwLabel title={"data_sync"} isBold={true} isUnderline={true} />
                        <div className={"ms-2"}>
                            <i className={"fad fa-info-circle"} /> Using Queue Table
                        </div>
                    </div>
                    <div className={"secondary-bg-color rounded-5 px-3 py-2 mb-3 d-inline-flex"}>
                        <div>
                            <Link className={"btn btn-sm btn-primary"} to={`/business/integrations/qbd/logs`}>
                                <i className={"fad fa-magnifying-glass"} />{" "}
                                <OwTranslate id={"view_queue_logs"} defaultMessage={"View Queue Logs"} />
                            </Link>
                        </div>
                        <div className={"ms-2"}>
                            <OwButton
                                leftIconClass={"fad fa-clock"}
                                title={"setup_data_pull"}
                                active={activeManualSync}
                                disableOnActive={true}
                                fn={showDataPullModal}
                            />
                        </div>

                        <div className={"ms-2"}>
                            <VerticalLine className={""} />
                        </div>

                        <div className={"ms-2"}>
                            <OwButton
                                className={"btn-warning"}
                                leftIconClass={"fad fa-rotate-left"}
                                title={"reset_queue"}
                                active={activeManualSync}
                                disableOnActive={true}
                                fn={resetQueue}
                            />
                        </div>

                        <div className={"ms-2"}>
                            <VerticalLine className={""} />
                        </div>

                        <div className={"ms-2"}>
                            <OwButton
                                className={"btn-primary"}
                                leftIconClass={"fad fa-refresh"}
                                title={"refresh_queue"}
                                active={isActiveRefreshQueue}
                                disableOnActive={true}
                                fn={getQueueItems}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
            <div>
                <div className={"mb-2"}>
                    <OwLabel title={"actions_on_next_update"} isBold={true} />
                    {hasActionChanges ? (
                        <div>
                            <OwButton className={"btn-primary me-2"} title={"save"} fn={saveActionChanges} />
                            <OwButton className={"btn-danger"} title={"reset"} fn={resetActionChanges} />
                        </div>
                    ) : null}
                </div>

                <div>
                    <div className={"card mb-2 " + props.borderClass}>
                        <div className={"card-header"}>
                            <OwLabel title={"push_actions"} isBold={true} />
                        </div>

                        <div className={"card-body"}>
                            <div>
                                {(actionsOnNextUpdate?.push?.Catalog || []).length ? (
                                    <div>
                                        <i
                                            className={"me-2 text-danger fas fa-times pointer"}
                                            onClick={() => {
                                                removeAction("push", "Catalog");
                                            }}></i>
                                        <OwLabel title={"catalog_items"} postText={":"} className={"me-2"} />{" "}
                                        {getCatalogNameFromPushCatalog()}
                                    </div>
                                ) : null}
                            </div>
                            <div>
                                {actionsOnNextUpdate?.push?.Customers ? (
                                    <div>
                                        <i
                                            className={"me-2 text-danger fas fa-times pointer"}
                                            onClick={() => {
                                                removeAction("push", "Customers");
                                            }}></i>
                                        <OwLabel title={"vendors"} supplierModel={supplierModel} isSupplierVendorOverride={true} />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={"card mb-2 " + props.borderClass}>
                    <div className={"card-header"}>
                        <OwLabel title={"update_actions"} isBold={true} />
                    </div>

                    <div className={"card-body"}>
                        <div className={""}>
                            <OwLabel title={"catalog_items"} className={"me-2"} postText={":"} isBold={true} />
                            {updateCatalogItems.length}
                        </div>
                        <div className={"mt-2"}>
                            <OwLabel title={"customers"} className={"me-2"} postText={":"} isBold={true} />
                            {actionsOnNextUpdate.update?.Customer?.length || 0}
                        </div>
                    </div>
                </div>

                <div className={"card mb-2 " + props.borderClass}>
                    <div className={"card-header"}>
                        <OwLabel title={"read_actions"} isBold={true} />
                    </div>

                    <div className={"card-body"}>
                        <div>
                            {readQueueItems.map((item, index) => {
                                const actionOptions = processActionOptions(item);

                                return (
                                    <div key={index}>
                                        <i
                                            className={"me-2 text-danger fas fa-times pointer"}
                                            onClick={() => {
                                                removeQueueItem(item.guid);
                                            }}></i>
                                        {processActionName(item.qbd_action)}
                                        {actionOptions ? <> - {actionOptions}</> : null}
                                    </div>
                                );
                            })}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.Customers ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "Customers");
                                        }}></i>
                                    <OwLabel title={"customers"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.TermOptions ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "TermOptions");
                                        }}></i>
                                    <OwLabel title={"term_options"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.CatalogItems ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "CatalogItems");
                                        }}></i>
                                    <OwLabel title={"catalog_items"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.SalesTaxOptions ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "SalesTaxOptions");
                                        }}></i>
                                    <OwLabel title={"sales_tax_options"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.SalesTaxItems ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "SalesTaxItems");
                                        }}></i>
                                    <OwLabel title={"sales_tax_items"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.COGSAccountOptions ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "COGSAccountOptions");
                                        }}></i>
                                    <OwLabel title={"cogs_account_options"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.AssetAccountOptions ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "AssetAccountOptions");
                                        }}></i>
                                    <OwLabel title={"asset_account_options"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.IncomeAccountOptions ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "IncomeAccountOptions");
                                        }}></i>
                                    <OwLabel title={"income_account_options"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.Templates ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "Templates");
                                        }}></i>
                                    <OwLabel title={"templates"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.SalesReps ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "SalesReps");
                                        }}></i>
                                    <OwLabel title={"sales_reps"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.ItemSubTotals ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "ItemSubTotals");
                                        }}></i>
                                    <OwLabel title={"item_subtotals"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.PriceLevels ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "PriceLevels");
                                        }}></i>
                                    <OwLabel title={"price_levels"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.UnitOfMeasureOptions ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "UnitOfMeasureOptions");
                                        }}></i>
                                    <OwLabel title={"unit_of_measure"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.ItemSites ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "ItemSites");
                                        }}></i>
                                    <OwLabel title={"item_sites"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.Class ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "Class");
                                        }}></i>
                                    <OwLabel title={"class"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.ShipMethod ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "ShipMethod");
                                        }}></i>
                                    <OwLabel title={"ship_method"} />
                                </div>
                            ) : null}
                        </div>
                        <div>
                            {actionsOnNextUpdate?.read?.DataExtDefs ? (
                                <div>
                                    <i
                                        className={"me-2 text-danger fas fa-times pointer"}
                                        onClick={() => {
                                            removeAction("read", "DataExtDefs");
                                        }}></i>
                                    <OwLabel title={"custom_fields"} />
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className={"card mb-2 " + props.borderClass}>
                    <div className={"card-header"}>
                        <OwLabel title={"create_actions"} isBold={true} />
                    </div>

                    <div className={"card-body"}>
                        <OwLabel
                            leftIconClass={"fad fa-list-alt text-success"}
                            title={"sales_orders"}
                            isBold={true}
                            postText={":"}
                            className={"me-2"}
                        />
                        <div>
                            {createQueueItems
                                .filter((item) => item.qbd_action === QBDQueueActionEnum.CREATE_SALES_ORDER)
                                .map((item, index) => {
                                    return (
                                        <div className={"d-flex align-items-center"} key={index}>
                                            <i
                                                className={"me-2 text-danger fas fa-times pointer"}
                                                onClick={() => {
                                                    removeQueueItem(item.guid);
                                                }}></i>
                                            {item.order_name}

                                            {uiContext.showHiddenFeatures && (
                                                <div>
                                                    <OwButton
                                                        title={"console_xml"}
                                                        fn={() => {
                                                            getOrderXml(item);
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>

                        <hr />
                        <div className={"mb-2"}>
                            <OwLabel
                                leftIconClass={"fad fa-file-invoice text-success"}
                                title={"invoices"}
                                isBold={true}
                                postText={":"}
                                className={"me-2"}
                            />
                            <div>
                                {createQueueItems
                                    .filter((item) => item.qbd_action === QBDQueueActionEnum.CREATE_INVOICE)
                                    .map((item, index) => {
                                        return (
                                            <div className={"d-flex align-items-center"} key={index}>
                                                <i
                                                    className={"me-2 text-danger fas fa-times pointer"}
                                                    onClick={() => {
                                                        removeQueueItem(item.guid);
                                                    }}></i>
                                                {item.order_name}

                                                {uiContext.showHiddenFeatures && (
                                                    <div>
                                                        <OwButton
                                                            title={"console_xml"}
                                                            fn={() => {
                                                                getOrderXml(item);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                            <hr />
                            <div className={"mb-2"}>
                                <OwLabel
                                    leftIconClass={"fad fa-books text-success"}
                                    isBold={true}
                                    title={"catalog_items"}
                                    postText={":"}
                                    className={"me-2"}
                                    noBottomMargin={true}
                                />
                                {createQueueItems
                                    .filter((item) => item.qbd_action === QBDQueueActionEnum.CREATE_CATALOG_ITEM)
                                    .map((item, index) => {
                                        return (
                                            <div key={index}>
                                                <i
                                                    className={"me-2 text-danger fas fa-times pointer"}
                                                    onClick={() => {
                                                        removeQueueItem(item.guid);
                                                    }}></i>
                                                {item.catalog_item_name}
                                            </div>
                                        );
                                    })}
                            </div>
                            <hr />
                            <div className={"mb-2"}>
                                <OwLabel
                                    leftIconClass={"fad fa-list-alt text-success"}
                                    title={"customers"}
                                    isBold={true}
                                    postText={":"}
                                    className={"me-2"}
                                />
                                <div>
                                    {createQueueItems
                                        .filter((item) => item.qbd_action === QBDQueueActionEnum.CREATE_CUSTOMER)
                                        .map((item, index) => {
                                            return (
                                                <div key={index}>
                                                    <i
                                                        className={"me-2 text-danger fas fa-times pointer"}
                                                        onClick={() => {
                                                            removeQueueItem(item.guid);
                                                        }}></i>
                                                    {item.vendor_name}
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                            <hr />
                            <div className={"mb-2"}>
                                <OwLabel
                                    leftIconClass={"fad fa-file-invoice text-success"}
                                    title={"purchase_orders"}
                                    isBold={true}
                                    postText={":"}
                                    className={"me-2"}
                                />
                                <div>
                                    {createQueueItems
                                        .filter((item) => item.qbd_action === QBDQueueActionEnum.CREATE_PURCHASE_ORDER)
                                        .map((item, index) => {
                                            return (
                                                <div key={index}>
                                                    <i
                                                        className={"me-2 text-danger fas fa-times pointer"}
                                                        onClick={() => {
                                                            removeQueueItem(item.guid);
                                                        }}></i>
                                                    {item.po_name}
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <OwLabel showIsReqAsterik={true} title={"requirements"} />:
                <div>
                    <ul>
                        <li>QuickBooks Desktop / Enterprise</li>
                        <li>
                            <OwLabel title={"always_on_pc_auto_update_only"} />
                        </li>
                        <li>
                            <a
                                className={"pointer"}
                                href={
                                    "https://developer.intuit.com/app/developer/qbdesktop/docs/get-started/get-started-with-quickbooks-web-connector#download-and-install-the-quickbooks-web-connector"
                                }
                                target={"_blank"}
                                rel="noreferrer">
                                <OwLabel title={"quickbooks_desktop_web_connector"} />
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};
